import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Sparkles, Package, Calendar, Star, FileText, Newspaper, LogOut } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({
    perfumes: 0,
    appointments: 0,
    testimonials: 0,
    articles: 0,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [perfumes, appointments, testimonials, articles] = await Promise.all([
        axios.get(`${API}/perfumes`),
        axios.get(`${API}/appointments`, { withCredentials: true }),
        axios.get(`${API}/testimonials?approved_only=false`, { withCredentials: true }),
        axios.get(`${API}/articles?published_only=false`, { withCredentials: true }),
      ]);

      setStats({
        perfumes: perfumes.data.length,
        appointments: appointments.data.length,
        testimonials: testimonials.data.filter((t) => !t.approved).length,
        articles: articles.data.length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: <Package size={32} />, title: 'Parfums', path: '/admin/perfumes', count: stats.perfumes, color: '#5C2018' },
    { icon: <Calendar size={32} />, title: 'Rendez-vous', path: '/admin/appointments', count: stats.appointments, color: '#1E7A6A' },
    { icon: <Star size={32} />, title: 'Avis Clients', path: '/admin/testimonials', count: stats.testimonials + ' en attente', color: '#B8922A' },
    { icon: <FileText size={32} />, title: 'Articles', path: '/admin/articles', count: stats.articles, color: '#1E7A6A' },
    { icon: <Newspaper size={32} />, title: 'Nouveautés', path: '/admin/news', count: '', color: '#5C2018' },
  ];

  return (
    <div className="min-h-screen bg-[#FDFAF4]">
      {/* Header */}
      <header className="bg-white border-b border-[#B8922A]/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/logo-harmony.jpg" alt="Harmony" className="h-12 w-12 rounded-full object-cover" />
            <span
              className="text-2xl font-light text-[#5C2018]"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
            >
              Harmony Parfumerie
            </span>
          </div>
          <div className="flex items-center gap-6">
            <span className="text-sm text-[#332724]">Bonjour, {user?.name}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-sm text-[#5C2018] hover:text-[#1E7A6A] transition-colors"
              data-testid="admin-logout"
            >
              <LogOut size={18} />
              <span>Déconnexion</span>
            </button>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <h1
          className="text-5xl font-light mb-4 text-[#5C2018]"
          style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
          data-testid="admin-dashboard-title"
        >
          Tableau de Bord
        </h1>
        <p className="text-[#332724] font-light mb-12">Bienvenue dans l'interface d'administration de Harmony Parfumerie</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {menuItems.map((item, index) => (
            <Link
              key={index}
              to={item.path}
              className="perfume-card bg-white p-8 rounded-lg border border-gold shadow-[0_8px_32px_rgba(92, 32, 24,0.05)]"
              data-testid={`admin-menu-${item.title.toLowerCase().replace(' ', '-')}`}
            >
              <div className="mb-4" style={{ color: item.color }}>
                {item.icon}
              </div>
              <h3
                className="text-2xl mb-2"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: 'italic',
                  color: item.color,
                }}
              >
                {item.title}
              </h3>
              {item.count !== '' && (
                <p className="text-3xl font-light text-[#332724]">{item.count}</p>
              )}
            </Link>
          ))}
        </div>

        {/* Outils & ressources */}
        <div className="mt-16">
          <h2 className="text-xs uppercase tracking-[0.3em] text-[#B8922A] mb-4">Outils & ressources</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/carte"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-br from-[#5C2018] to-[#2E120D] text-[#FDFAF4] p-5 rounded-xl hover:opacity-90 transition-opacity flex items-center gap-4"
              data-testid="admin-tool-carte"
            >
              <div className="w-12 h-12 rounded-full bg-[#B8922A]/20 flex items-center justify-center text-2xl">🎴</div>
              <div className="flex-1">
                <p className="text-sm font-medium">Carte de visite</p>
                <p className="text-[11px] text-[#FDFAF4]/70">Voir & télécharger PDF/PNG</p>
              </div>
            </Link>
            <Link
              to="/admin/loyalty"
              className="bg-gradient-to-br from-[#1E7A6A] to-[#0f4d44] text-[#FDFAF4] p-5 rounded-xl hover:opacity-90 transition-opacity flex items-center gap-4"
              data-testid="admin-tool-loyalty"
            >
              <div className="w-12 h-12 rounded-full bg-[#B8922A]/20 flex items-center justify-center text-2xl">🏆</div>
              <div className="flex-1">
                <p className="text-sm font-medium">Cercle fidélité</p>
                <p className="text-[11px] text-[#FDFAF4]/70">Points · Anniversaires · Sync Square</p>
              </div>
            </Link>
            <Link
              to="/fidelite"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-br from-[#B8922A] to-[#8a6c1c] text-[#FDFAF4] p-5 rounded-xl hover:opacity-90 transition-opacity flex items-center gap-4"
              data-testid="admin-tool-fidelite-public"
            >
              <div className="w-12 h-12 rounded-full bg-[#FDFAF4]/20 flex items-center justify-center text-2xl">✨</div>
              <div className="flex-1">
                <p className="text-sm font-medium">Voir page fidélité publique</p>
                <p className="text-[11px] text-[#FDFAF4]/70">Pour partage aux clients</p>
              </div>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
